import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Components
import Select from '../../../../components/atoms/select';
import Button from '../../../../components/atoms/button';
import Table, { TableColumn } from '../../../../components/organisms/table';
import Text from '../../../../components/atoms/text';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

// Services
import YardService, { IYard } from '../../../../services/Yard.service';

// Utils & Types
import { EditableChangeEvent } from '../../../../utils/types/editable';
import { dateObjToStr } from '../../../../utils/date';
import { HomeLoaderData } from '../../../../types/homeLoaderData';
import ContainerService, {
	IContainer,
} from '../../../../services/Container.service';
import Input from '../../../../components/atoms/input';
import MonitoringService, {
	MonitoringR,
} from '../../../../services/Monitoring.service';
import InputCalendar from '../../../../components/molecules/input-calendar';

// ================ Constants & Types ================
interface ContainerForm {
	yard: Partial<IYard>;
}

const DEFAULT_FORM: ContainerForm = {
	yard: {},
};

const DEFAULT_PAGINATION: PaginationProps = {
	pageCurrent: 1,
	pageSize: 10,
	totalItems: 0,
	totalPages: 1,
};

interface ContainerR extends IContainer {
	checked: boolean;
	travelCode?: string;
}

// ================ Main Component ================
const AuditoryContainer = () => {
	const { t } = useTranslation();
	const { accountId } = useRouteLoaderData('home') as HomeLoaderData;

	// ================ State ================
	const [yards, setYards] = useState<IYard[]>([]);
	const [form, setForm] = useState<ContainerForm>(DEFAULT_FORM);
	const [searchTrigger, setSearchTrigger] = useState<boolean>(false);
	const [containers, setContainers] = useState<ContainerR[]>([]);
	const [pagination, setPagination] =
		useState<PaginationProps>(DEFAULT_PAGINATION);

	// ================ Table Columns ================
	const columns: TableColumn<ContainerR>[] = useMemo(
		() => [
			{
				key: 'actions',
				label: t('misc.remove'),
				children: (row) => (
					<input
						type="checkbox"
						checked={row.checked}
						onClick={() => handleCheck(row)}
					/>
				),
			},
			{ key: 'label', label: t('port_management.container') },
			{ key: 'carrier', label: t('port_management.carrier') },
			{
				key: 'dateArrive',
				label: t('date.title'),
				children: (row) => (
					<Text className="text-xs">
						{dateObjToStr(row.dateArrive, true)}
					</Text>
				),
			},
			{
				key: 'temperature',
				label: t('port_management.containers.set_point'),
			},
			{
				key: 'movement',
				label: t('movement.travel_code'),
				children: (row) => (
					<Text className="text-xs">{row.movement.travelCode}</Text>
				),
			},
		],
		[t, containers]
	);

	const fetchYards = useCallback(async () => {
		if (!accountId) return;

		try {
			const response = await YardService.getAll({
				isActive: 1,
				idClient: Number(accountId),
			});

			if (response?.data) {
				setYards(response.data);
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_yards'));
		}
	}, [accountId, t]);

	const fetchContainer = useCallback(async () => {
		if (!form.yard.id) return;
		try {
			const response = await ContainerService.getAll(
				{
					pageCurrent: pagination.pageCurrent,
					pageSize: pagination.pageSize,
					idClient: Number(accountId),
				},
				form.yard?.id
			);

			if (response) {
				setContainers(
					response.data.map((container) => ({
						...container,
						checked: false,
					}))
				);
				setPagination((prev) => ({
					...prev,
					totalItems: response.totalItems,
					totalPages: response.totalPages,
				}));
			}
		} catch (error) {
			NotificationToast.Warn(t('errors.fetch_movements'));
		}
	}, [pagination.pageCurrent, pagination.pageSize, searchTrigger]);

	// ================ Effects ================
	useEffect(() => {
		fetchYards();
	}, [fetchYards]);

	useEffect(() => {
		fetchContainer();
	}, [fetchContainer]);

	// ================ Handlers ================
	const handleCheck = (container: ContainerR) => {
		const index = containers.findIndex((i) => i.id == container.id);
		const newData = [...containers];
		newData[index].checked = !newData[index].checked;
		setContainers(newData);
	};

	const handleYardChange = useCallback(
		(e: EditableChangeEvent) => {
			const yard = yards.find((y) => y.name === e.target.value);
			if (!yard) return;

			setForm((prev) => ({ ...prev, yard }));
		},
		[yards]
	);

	const handleSearch = useCallback(() => {
		setSearchTrigger((prev) => !prev);
	}, []);

	const handleResetFilters = useCallback(() => {
		// Reset form to default values
		setForm(DEFAULT_FORM);
		// Trigger search to show all results
		setSearchTrigger((prev) => !prev);
		// Reset to first page
		setPagination((prev) => ({ ...prev, pageCurrent: 1 }));
	}, []);

	const handlePaginationUpdate = useCallback(
		(pageSize: number, pageCurrent: number) => {
			setPagination((prev) => ({ ...prev, pageSize, pageCurrent }));
		},
		[]
	);

	const handleSave = async () => {
		const ids = [];
		for (let container of containers) {
			if (container.checked) ids.push(container.id);
		}
		if (ids.length == 0) {
			NotificationToast.Warn(
				t('port_management.monitoring.not_selected')
			);
			return;
		}
		const response = await ContainerService.deactivate(ids);
		if (response) {
			NotificationToast.Success(t('alert.success.generic'));
			handleSearch();
		}
	};

	const handleDateChange = (date: Date) => {
		setForm((prev) => ({ ...prev, dateRetroactive: date }));
	};

	// ================ Render Helpers ================
	const renderFilters = () => (
		<div className="flex gap-x-8 gap-y-4 flex-wrap items-end">
			<Select
				boxClassName="min-w-60"
				name="yard"
				value={form.yard.name || ''}
				onChange={handleYardChange}
				label={t('port_management.yard')}
				placeholder={t('port_management.yard')}
				options={yards.map((y) => y.name)}
				disabled={!accountId}
			/>

			<div className="flex gap-2">
				<Button onClick={handleSearch}>{t('misc.search')}</Button>

				<Button theme="light" onClick={handleResetFilters}>
					{t('misc.reset')}
				</Button>
			</div>
		</div>
	);

	const renderTable = () => (
		<div className="overflow-x-auto w-full flex flex-col gap-8">
			<Table
				data={containers}
				columns={columns}
				className={classNames('grid-cols-[auto_1fr_1fr_1fr_1fr_1fr]')}
			/>
		</div>
	);

	// ================ Render ================
	return (
		<div className="flex flex-col gap-4 px-8 py-4">
			{renderFilters()}
			{renderTable()}
			<Button className="w-fit self-end" onClick={handleSave}>
				{t('misc.save')}
			</Button>
			<Pagination {...pagination} onUpdate={handlePaginationUpdate} />
		</div>
	);
};

export default AuditoryContainer;
